import { z } from 'zod';
/**
 * Card primitives shared by the baraha engines (Tongits, Pusoy Dos, Pusoy).
 *
 * A card on the wire is a compact string: rank + suit, e.g. "AS", "10H", "QD".
 * Ported from the live Baraha Pa More server so the encoding stays identical
 * across the two products — the same golden vectors and the same client-side
 * card parsing work against either.
 */
export const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
export const SUITS = ['S', 'H', 'D', 'C'];
const CARD_RE = /^(?:A|K|Q|J|10|[2-9])[SHDC]$/;
/** Wire-level validation for anything a client claims is a card. */
export const CardCode = z.string().regex(CARD_RE, 'hindi baraha');
export function isCardCode(x) {
    return typeof x === 'string' && CARD_RE.test(x);
}
/** Distinct, well-formed, non-empty — what every "cards you played" payload must be. */
export function isCardArray(arr) {
    if (!Array.isArray(arr) || arr.length === 0)
        return false;
    const seen = new Set();
    for (const c of arr) {
        if (!isCardCode(c) || seen.has(c))
            return false;
        seen.add(c);
    }
    return true;
}
export function standardDeck() {
    const deck = [];
    for (const s of SUITS)
        for (const r of RANKS)
            deck.push(r + s);
    return deck;
}
export function rankOf(code) {
    return code.slice(0, -1);
}
export function suitOf(code) {
    return code.slice(-1);
}
/** A=14 high. Used where the ace tops the rank order. */
export function rankValueAceHigh(code) {
    const r = rankOf(code);
    if (r === 'A')
        return 14;
    if (r === 'K')
        return 13;
    if (r === 'Q')
        return 12;
    if (r === 'J')
        return 11;
    return Number(r);
}
/** A=1 low. Used for runs where the ace sits below the two. */
export function rankOrdinalAceLow(code) {
    return RANKS.indexOf(rankOf(code));
}
/**
 * Tongits deadwood value: pips at face value, court cards 10, ace 1.
 * Lower is better, so this is the number a player is trying to shed.
 */
export function tongitsPoints(code) {
    const r = rankOf(code);
    if (r === 'A')
        return 1;
    if (r === 'J' || r === 'Q' || r === 'K')
        return 10;
    return Number(r);
}
/**
 * Deals from a deck shuffled by the injected seeded RNG.
 *
 * The engine never draws its own randomness: the server publishes SHA-256 of
 * the seed before the deal and reveals the seed at settlement, so the shuffle
 * has to be reproducible from the seed alone (PRD §8 commit-reveal).
 */
export function shuffledDeck(rng) {
    return rng.shuffle(standardDeck());
}
/** Stable display order: by suit, then rank. Keeps a hand from jumping around. */
export function handSort(a, b) {
    const s = SUITS.indexOf(suitOf(a)) - SUITS.indexOf(suitOf(b));
    return s !== 0 ? s : rankOrdinalAceLow(a) - rankOrdinalAceLow(b);
}
//# sourceMappingURL=cards.js.map